// Изгледът на решетката „Инвентаризация" — чиста функция над пресметнатите
// редове: трите блока (горе „За броене" — закачените от „Целия списък" и от
// „Нов продукт", още непреброени; после „Преброени"; накрая „Непреброени"),
// филтрите на изгледа (група, „Скрий без наличност", статус) и броячите.
//
// ЕДНИ И СЪЩИ числа навсякъде: броячът на чипа за статус и броят в лентата на
// блока идват от `statusCounts` — чипът „Непреброени 3" и лентата
// „НЕПРЕБРОЕНИ · 3" не могат да се разминат (закаченото НЕ е непреброено,
// то е „За броене"). „Всички" = За броене + Преброени + Непреброени; „С
// разлика" е част от преброените.
//
// Филтрите режат САМО показаните редове — `countedRows`, сумите и записът
// тръгват от `allRows` (docs/inventory.md §Капани), затова скрит ред не
// изпада от документа.
//
// Статусът на реда е „УСТАНОВЕНИЯТ" — намереното към последното НАПУСКАНЕ на
// клетката (`settledCountedById`), не към всяко натискане. Иначе при филтър
// „С разлика" редът изчезва изпод пръстите, щом написаното стане равно на
// търсеното, а закаченият ред би скочил в „Преброени" още с първата цифра.
// Наличността и взаимозамените обаче се четат живи: смяна на датата или
// „Замени" местят реда веднага (там никой не пише в него). Живо се чете и
// частта в приготвените рецепти (`inRecipesQty` — пише се в реда на рецептата).
//
// Съставка на приготвена рецепта без насипно преброено (`needsLooseCount`) е
// „За броене" и без да е избрана от списъка — след закачените, в реда на
// рецептите: ако не се преброи и извън рецептата (или 0), целият ѝ насипен
// запас става липса.
import type { Product } from "../../../../api/inventory";
import {
  diffKindOf,
  groupKeyOf,
  isRowCounted,
  type StocktakeComputedRow,
} from "./stocktakeWorksheetShared";

export type StocktakeStatusFilter = "all" | "pinned" | "counted" | "uncounted" | "diff";
// Редът на чиповете над таблицата („За броене" се показва само когато има)
export const STOCKTAKE_STATUS_FILTERS: StocktakeStatusFilter[] = [
  "all",
  "pinned",
  "counted",
  "uncounted",
  "diff",
];

export type StocktakeViewFilters = {
  groupFilter: string | null; // null = всички групи; "" = без група
  hideZeroStock: boolean;
  statusFilter: StocktakeStatusFilter;
  // Избраните от търсачката / новосъздадените: видими при ВСЕКИ филтър —
  // иначе скокът води към ред, който го няма на екрана.
  revealedProductIds: Set<number>;
  // Закачените (в реда на избора): блокът „За броене", докато не бъдат
  // преброени; групата и „Скрий без наличност" не ги крият.
  pinnedProductIds: number[];
  // Продуктите-съставки на приготвените рецепти в реда на рецептите; тези без
  // насипно преброено застават в „За броене" след закачените
  recipeIngredientOrder?: number[];
};

export type StocktakeStatusCounts = Record<StocktakeStatusFilter, number>;

// Трите блока в реда на екрана; „rest" = непреброените
export type StocktakeBlockKey = "pinned" | "counted" | "rest";
export type StocktakeBlock = {
  key: StocktakeBlockKey;
  rows: StocktakeComputedRow[];
  // броят в лентата — СЪЩИЯТ като на чипа за статус (`statusCounts`)
  count: number;
  // „Преброени · N от M": M = всички (по групата и „Скрий без наличност")
  total: number;
  startNumber: number; // номерът („#") на първия ред в блока
  // „За броене": откъде са редовете — избрани от списъка и/или съставки на
  // рецептите (подсказката в лентата казва кое)
  pinnedFrom?: { list: boolean; recipes: boolean };
};

export type StocktakeView = {
  // Подредени за показване: за броене · преброени · с наличност · без наличност
  displayRows: StocktakeComputedRow[];
  // Непразните блокове — решетката и картите ги рендират с ленти-заглавия
  blocks: StocktakeBlock[];
  // Броячите на чиповете — по ОСТАНАЛИТЕ филтри (група, без наличност), не по
  // самия статус; „Преброени N" от „Всички M" е и напредъкът в лентата.
  statusCounts: StocktakeStatusCounts;
};

// Чипът за статус на всеки блок (броят в лентата = броят на чипа)
export const BLOCK_STATUS: Record<StocktakeBlockKey, StocktakeStatusFilter> = {
  pinned: "pinned",
  counted: "counted",
  rest: "uncounted",
};

type RowStatus = { block: StocktakeBlockKey; hasDiff: boolean };

// Установеното състояние на реда: в кой блок е и има ли разлика
const settledStatusOf = (
  row: StocktakeComputedRow,
  settledCountedById: Map<number, number>,
  adjustments: Map<number, number>,
  isPinned: boolean,
): RowStatus => {
  const settled = settledCountedById.get(row.product.id);
  if (settled == null) return { block: isPinned ? "pinned" : "rest", hasDiff: false };
  const difference =
    settled + (row.inRecipesQty ?? 0) - row.systemQty + (adjustments.get(row.product.id) ?? 0);
  return { block: "counted", hasDiff: diffKindOf(difference) !== "equal" };
};

const statusMatches = (filter: StocktakeStatusFilter, status: RowStatus) =>
  filter === "all" ||
  (filter === "diff" ? status.hasDiff : BLOCK_STATUS[status.block] === filter);

export function buildStocktakeView(
  allRows: StocktakeComputedRow[],
  settledCountedById: Map<number, number>,
  adjustments: Map<number, number>,
  filters: StocktakeViewFilters,
): StocktakeView {
  const { groupFilter, hideZeroStock, statusFilter, revealedProductIds, pinnedProductIds } =
    filters;
  // Закачените от списъка, после съставките без насипно преброено (в реда на
  // рецептите — `allRows` е по име, затова редът идва от `recipeIngredientOrder`)
  const pinnedOrder = new Map(pinnedProductIds.map((id, index) => [id, index]));
  const ingredientOrder = filters.recipeIngredientOrder ?? [];
  if (ingredientOrder.length) {
    const rowById = new Map(allRows.map((row) => [row.product.id, row]));
    for (const productId of ingredientOrder) {
      if (rowById.get(productId)?.needsLooseCount && !pinnedOrder.has(productId))
        pinnedOrder.set(productId, pinnedOrder.size);
    }
  }
  const pinned: StocktakeComputedRow[] = [];
  const counted: StocktakeComputedRow[] = [];
  const inStock: StocktakeComputedRow[] = [];
  const outOfStock: StocktakeComputedRow[] = [];
  const statusCounts: StocktakeStatusCounts = {
    all: 0,
    pinned: 0,
    counted: 0,
    uncounted: 0,
    diff: 0,
  };

  for (const row of allRows) {
    const productId = row.product.id;
    const revealed = revealedProductIds.has(productId);
    const status = settledStatusOf(
      row,
      settledCountedById,
      adjustments,
      pinnedOrder.has(productId),
    );
    // Групата: чипът крие другите групи (и преброеното там — то остава в
    // записа); избраното от търсачката и закаченото остават видими.
    if (
      groupFilter != null &&
      groupKeyOf(row.product) !== groupFilter &&
      !revealed &&
      status.block !== "pinned"
    )
      continue;
    // „Скрий без наличност" маха САМО неизползваните нулеви редове: вече
    // писаното, закаченото и избраното от търсачката остават.
    if (
      hideZeroStock &&
      status.block === "rest" &&
      row.systemQty <= 0 &&
      !isRowCounted(row.draft) &&
      !revealed
    )
      continue;
    statusCounts.all += 1;
    statusCounts[BLOCK_STATUS[status.block]] += 1;
    if (status.hasDiff) statusCounts.diff += 1;
    if (!revealed && !statusMatches(statusFilter, status)) continue;
    if (status.block === "pinned") pinned.push(row);
    else if (status.block === "counted") counted.push(row);
    else if (row.systemQty > 0) inStock.push(row);
    else outOfStock.push(row);
  }
  pinned.sort((a, b) => pinnedOrder.get(a.product.id)! - pinnedOrder.get(b.product.id)!);

  const blocks: StocktakeBlock[] = [];
  let startNumber = 1;
  const addBlock = (key: StocktakeBlockKey, rows: StocktakeComputedRow[]) => {
    if (rows.length === 0) return;
    blocks.push({
      key,
      rows,
      count: statusCounts[BLOCK_STATUS[key]],
      total: statusCounts.all,
      startNumber,
    });
    startNumber += rows.length;
  };
  addBlock("pinned", pinned);
  if (blocks[0]?.key === "pinned") {
    const listed = new Set(pinnedProductIds);
    blocks[0].pinnedFrom = {
      list: pinned.some((row) => listed.has(row.product.id)),
      recipes: pinned.some((row) => !listed.has(row.product.id)),
    };
  }
  addBlock("counted", counted);
  addBlock("rest", [...inStock, ...outOfStock]);
  return {
    displayRows: blocks.flatMap((block) => block.rows),
    blocks,
    statusCounts,
  };
}

// Партидата и срокът на НОВАТА партида (само при излишък): полетата се питат
// сами САМО за продукт, който ги следи (или вече има написано / редът е
// отворен ръчно); иначе под името стои малката връзка „+ партида / срок".
export type StocktakeBatchState = "none" | "link" | "fields";
export const batchStateOf = (
  row: StocktakeComputedRow,
  openedProductIds: ReadonlySet<number>,
): StocktakeBatchState => {
  if (row.kind !== "surplus") return "none";
  const { product, draft } = row;
  if (
    product.trackBatch ||
    product.trackExpiry ||
    draft.batch.trim() !== "" ||
    draft.expiry !== "" ||
    openedProductIds.has(product.id)
  )
    return "fields";
  return "link";
};

// Групите за чиповете: ключ → брой продукти (подредени по име; „без група"
// накрая). Броят е по целия каталог — не зависи от другите филтри.
export function stocktakeProductGroups(products: Product[]): { key: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const product of products) {
    const key = groupKeyOf(product);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b, "bg")))
    .map(([key, count]) => ({ key, count }));
}
